import { z } from 'zod'

/**
 * What a client has decided to do with one tree.
 *
 * Pruning and removal are competing answers to the same tree, not line items
 * to add up: you do not prune a tree you are taking down, and its problem
 * treatments go with it. So this is one choice rather than two toggles.
 *
 * Stored only where it DIFFERS from the default, so an empty selection prices
 * exactly what the proposal offered — which matters because the same
 * arithmetic runs in a browser and again on the server when it is accepted.
 */
export const TREE_WORK_CHOICES = ['PRUNE', 'REMOVE', 'NONE'] as const

export const treeWorkChoiceSchema = z.enum(TREE_WORK_CHOICES)

export type TreeWorkChoice = z.infer<typeof treeWorkChoiceSchema>

/**
 * How a choice that differs from the offer reads in the client's change list
 * and on the selection summary. Both lists have to say this the same way.
 */
export const TREE_WORK_CHOICE_PHRASES: Record<TreeWorkChoice, string> = {
  REMOVE: 'removed instead of pruned',
  PRUNE: 'pruned instead of removed',
  NONE: 'left for now',
}

/**
 * What a tree does when nobody has chosen.
 *
 * Pruning where it is offered, because it is the conservative answer and the
 * client opts up to removal rather than being defaulted into it. A tree quoted
 * ONLY for removal defaults to removal — leaving it out would open the
 * proposal understating its own total, and the arborist quoted it for a
 * reason.
 */
export function defaultWorkChoice(options: {
  hasPruning: boolean
  hasRemoval: boolean
}): TreeWorkChoice {
  if (options.hasPruning) return 'PRUNE'
  return options.hasRemoval ? 'REMOVE' : 'PRUNE'
}
